import {
  CreatePaymentIntentRequest,
  PaymentConfirmationRequest,
  PaymentIntentResponse,
  PaymentStatus,
} from "../types/payment";
import { isTokenExpired } from "../utils/jwtUtils";
import { withServiceFeeAlias } from "../utils/serviceFeePayload";

import apiClient from "./apiClient";

const PAYMENT_API_BASE = "/payment";

export const paymentService = {
  // Create a payment intent
  async createPaymentIntent(
    request: CreatePaymentIntentRequest,
  ): Promise<PaymentIntentResponse> {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("User not authenticated. Please log in again.");
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        throw new Error("Authentication expired. Please log in again.");
      }

      const response = await apiClient.post(
        `${PAYMENT_API_BASE}/create-payment-intent`,
        withServiceFeeAlias(request),
      );

      // Handle the Result wrapper from backend
      if (response.data && response.data.isSuccess) {
        return response.data.data; // Extract the actual data from Result wrapper
      } else {
        throw new Error(
          response.data?.message || "Failed to create payment intent",
        );
      }
    } catch (error: any) {

      // Handle specific error cases without triggering automatic logout
      if (error.response?.status === 401) {
        // Check if it's a token issue
        if (
          error.response?.data?.error?.includes("token") ||
          error.response?.data?.message?.includes("token")
        ) {
          throw new Error("Authentication expired. Please log in again.");
        } else {
          throw new Error("Payment authorization failed. Please try again.");
        }
      } else if (error.response?.status === 403) {
        throw new Error(
          "Payment not authorized. Please check your payment method.",
        );
      } else if (error.response?.status === 404) {
        throw new Error(
          "Payment service not available. Please try again later.",
        );
      } else if (error.code === "ERR_NETWORK") {
        throw new Error(
          "Network error. Please check your connection and try again.",
        );
      } else if (error.response?.status === 422) {
        // Price validation failed - attach price update data to error
        const priceUpdateData = error.response?.data?.data;
        const errorWithData: any = new Error(
          error.response?.data?.message || "Product prices have been updated",
        );
        errorWithData.priceUpdateData = priceUpdateData;
        throw errorWithData;
      } else {
        // For other errors, throw the original error message
        throw new Error(
          error.response?.data?.error ||
            error.message ||
            "Payment failed. Please try again.",
        );
      }
    }
  },

  // Confirm a payment
  async confirmPayment(request: PaymentConfirmationRequest): Promise<boolean> {
    try {
      const response = await apiClient.post(
        `${PAYMENT_API_BASE}/confirm`,
        request,
      );

      // Handle the Result wrapper from backend
      if (response.data && response.data.isSuccess) {
        return response.data.data; // Extract the actual data from Result wrapper
      } else {
        throw new Error(
          response.data?.message || "Payment confirmation failed",
        );
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Payment confirmation failed.",
      );
    }
  },

  // Get payment status
  async getPaymentStatus(paymentIntentId: string): Promise<PaymentStatus> {
    try {
      const response = await apiClient.get(
        `${PAYMENT_API_BASE}/status/${paymentIntentId}`,
      );

      // Handle the Result wrapper from backend
      if (response.data && response.data.isSuccess) {
        return response.data.data; // Extract the actual data from Result wrapper
      } else {
        throw new Error(
          response.data?.message || "Failed to get payment status",
        );
      }
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to get payment status.",
      );
    }
  },
};
